import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { InputTextModule } from "primeng/inputtext";
import { Zone } from "../../../../../core/database/db";
import { ButtonModule } from "primeng/button";
import { MessageModule } from "primeng/message";
import { InputErrorComponent } from "../../../../../shared/components/input-error/input-error.component";

export interface EditRoomNameArea {
  id: number;
  zoneId: number;
  name: string;
  sortingOrder?: number;
}

export interface EditRoomNameUpdate {
  id: number;
  name: string;
}

type RoomFormGroup = FormGroup<{
  id: FormControl<number>;
  zoneId: FormControl<number>;
  currentName: FormControl<string>;
  name: FormControl<string>;
}>;

type EditRoomNamesForm = FormGroup<{
  rooms: FormArray<RoomFormGroup>;
}>;

@Component({
  selector: "app-edit-room-names",
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DialogModule, InputTextModule, ButtonModule, MessageModule, InputErrorComponent],
  templateUrl: "./edit-room-names.component.html",
  styleUrl: "./edit-room-names.component.scss",
})
export class EditRoomNamesComponent implements OnChanges {
  @Input() visible = false;
  @Input() zones: Zone[] = [];
  @Input() areas: EditRoomNameArea[] = [];
  isSaving = input<boolean>(false);

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveRoomNames = new EventEmitter<EditRoomNameUpdate[]>();

  @ViewChildren("roomInput")
  private roomInputs?: QueryList<ElementRef<HTMLInputElement>>;


  form: EditRoomNamesForm;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      rooms: this.fb.array<RoomFormGroup>([], {
        validators: [this.uniqueRoomNamesValidator()],
      }),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["areas"] || (changes["visible"] && this.visible)) {
      this.rebuildForm();
      this.form.markAllAsTouched();
      this.rooms.updateValueAndValidity({ emitEvent: false });
    }
  }

  get rooms(): FormArray<RoomFormGroup> {
    return this.form.controls.rooms;
  }

  getRoomsForZone(zoneId: number): RoomFormGroup[] {
    return this.rooms.controls.filter(control => control.controls.zoneId.value === zoneId);
  }

  trackByZone = (_index: number, zone: Zone): number => zone.id;

  trackByRoom = (_index: number, control: RoomFormGroup): number => control.controls.id.value;

  showClearButton(control: RoomFormGroup): boolean {
    return !!control.controls.name.value;
  }

  clearName(control: RoomFormGroup): void {
    control.controls.name.setValue("");
    control.controls.name.markAsTouched();
    this.rooms.updateValueAndValidity();
  }

  getControlIndex(control: RoomFormGroup): number {
    return this.rooms.controls.findIndex(current => current === control);
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.handleVisibleChange(visible);
  }

  onCancel(): void {
    this.handleVisibleChange(false);
  }

  onSave(): void {
    this.form.markAllAsTouched();
    this.rooms.updateValueAndValidity();

    if (this.form.invalid) {
      return;
    }

    const updates = this.rooms.controls
      .map(control => ({
        id: control.controls.id.value,
        currentName: control.controls.currentName.value.trim(),
        name: control.controls.name.value.trim(),
      }))
      .filter(control => control.name !== control.currentName)
      .map(control => ({
        id: control.id,
        name: control.name,
      }));

    this.saveRoomNames.emit(updates);
  }

  roomNameInvalid(control: RoomFormGroup): boolean {
    const nameControl = control.controls.name;
    return (nameControl.invalid || this.hasDuplicateName(control)) && nameControl.touched;
  }

  roomNameErrorText(control: RoomFormGroup): string {
    const nameControl = control.controls.name;

    if (nameControl.hasError("required") || nameControl.hasError("whitespace")) {
      return "Room name is required.";
    }

    if (nameControl.hasError("pattern")) {
      return "Room name contains invalid characters.";
    }

    if (this.hasDuplicateName(control)) {
      return "Room names must be unique.";
    }

    return "";
  }

  noWhiteSpaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isWhitespace = (control.value || "").trim().length === 0;
      return isWhitespace ? { whitespace: true } : null;
    };
  }

  uniqueRoomNamesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rooms = control as FormArray<RoomFormGroup>;
      const names = rooms.controls
        .map(room => this.normalizeRoomName(room.controls.name.value))
        .filter(name => !!name);

      return new Set(names).size !== names.length ? { duplicateRoomNames: true } : null;
    };
  }

  normalizeRoomName(value: string | null | undefined): string {
    return (value ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  hasDuplicateName(control: RoomFormGroup): boolean {
    const currentName = this.normalizeRoomName(control.controls.name.value);

    if (!currentName) {
      return false;
    }

    return this.rooms.controls.some(otherControl => {
      if (otherControl === control) {
        return false;
      }

      return this.normalizeRoomName(otherControl.controls.name.value) === currentName;
    });
  }

  getRoomTabIndex(zoneIndex: number, roomIndex: number): number {
    return zoneIndex * 100 + roomIndex + 1;
  }

  isLastRoomInDialog(zoneId: number, roomIndex: number): boolean {
    const zonesWithRooms = this.zones.filter(z => this.getRoomsForZone(z.id).length > 0);
    const lastZone = zonesWithRooms[zonesWithRooms.length - 1];

    if (!lastZone || lastZone.id !== zoneId) {
      return false;
    }

    const rooms = this.getRoomsForZone(zoneId);
    return roomIndex === rooms.length - 1;
  }

  getCancelTabIndex(): number {
    return 9000;
  }

  getSaveTabIndex(): number {
    return 9001;
  }

  private handleVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
  }

  private rebuildForm(): void {
    const sortedAreas = [...this.areas].sort((a, b) => {
      if (a.zoneId !== b.zoneId) {
        return a.zoneId - b.zoneId;
      }

      return (a.sortingOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortingOrder ?? Number.MAX_SAFE_INTEGER);
    });

    this.form.setControl(
      "rooms",
      this.fb.array(
        sortedAreas.map(area =>
          this.fb.group({
            id: this.fb.nonNullable.control(area.id ?? 0),
            zoneId: this.fb.nonNullable.control(area.zoneId ?? 0),
            currentName: this.fb.nonNullable.control(area.name ?? ""),
            name: this.fb.nonNullable.control(area.name ?? "", {
              validators: [
                Validators.required,
                Validators.pattern("^[0-9a-zA-Z' \\-/\\\\]{1,100}$"),
                this.noWhiteSpaceValidator(),
              ],
            }),
          })
        ),
        {
          validators: [this.uniqueRoomNamesValidator()],
        }
      )
    );
  }
}

